"use client"

import Image from "next/image"
import Link from "next/link"
import { useState, useContext, useEffect } from "react"
import { useParams } from "next/navigation"
import { Article } from "@/components/utils/interface"
import { BlogContext } from "@/app/context/blogContext"
import { logEvent } from "firebase/analytics"
import { analytics } from "@/app/firebase"
import { AnalyticsContext } from "@/app/context/analyticsContext"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXTwitter, faFacebookF, faLinkedinIn } from '@fortawesome/free-brands-svg-icons'
import { faLink } from "@fortawesome/free-solid-svg-icons";



import {
  CalendarDays,
  Clock,
  ArrowLeft,
  Share2,
  Heart,
  MessageCircle,
  Twitter,
  Facebook,
  Linkedin,
  LinkIcon,
  Send,
} from "lucide-react"


// Sample comments data
// const initialComments = [
//   {
//     id: 1,
//     author: "Alex Johnson",
//     authorImage: "/placeholder.svg?height=32&width=32&text=AJ",
//     content:
//       "Great article! The points about AI-powered development tools really resonate with my recent experience using GitHub Copilot. It's amazing how much it speeds up the development process.",
//     date: "March 16, 2024",
//     likes: 5,
//   },
//   {
//     id: 2,
//     author: "Maria Garcia",
//     authorImage: "/placeholder.svg?height=32&width=32&text=MG",
//     content:
//       "The section on sustainable web development is particularly important. We need more awareness about the environmental impact of our digital products. Thanks for highlighting this!",
//     date: "March 16, 2024",
//     likes: 8,
//   },
//   {
//     id: 3,
//     author: "David Kim",
//     authorImage: "/placeholder.svg?height=32&width=32&text=DK",
//     content:
//       "Edge computing is definitely a game-changer. I've been experimenting with Vercel Edge Functions and the performance improvements are remarkable, especially for global applications.",
//     date: "March 17, 2024",
//     likes: 3,
//   },
// ]

export default function BlogArticle() {
  // const [comments, setComments] = useState(initialComments)
  // const [newComment, setNewComment] = useState("")
  // const [isLiked, setIsLiked] = useState(false)
  // const [likesCount, setLikesCount] = useState(article1.likes)
  // const [isSubmittingComment, setIsSubmittingComment] = useState(false)

  const { blogId } = useParams(); // Get the slug from the URL
  const { articles } = useContext(BlogContext); // Access articles from context
  const [article, setArticle] = useState<Article | null>(null);
  const { trackEvent } = useContext(AnalyticsContext);

  useEffect(() => {
    // Find the article matching the blogId
    const foundArticle = articles.find(
      (art) => art.blogId === blogId
    );
    setArticle(foundArticle || null);
    // Track article view
    if (foundArticle) {
      trackEvent('view_article', {
        article_id: foundArticle.blogId,
        article_title: foundArticle.title,
        article_tag: foundArticle.tag,
        featured: foundArticle.featured,
      });
    }
  }, [articles, blogId, trackEvent]);

  

  if (!article) {
    return <div>Article not found</div>;
  }

  // const handleLike = () => {
  //   setIsLiked(!isLiked)
  //   setLikesCount((prev) => (isLiked ? prev - 1 : prev + 1))
  // }

  const handleShare = (platform: string) => {
    // event.preventDefault();
    const url = encodeURIComponent(window.location.href)
    const title = encodeURIComponent(article.title)
    const text = encodeURIComponent(article.excerpt)

    let shareUrl = ""

    switch (platform) {
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`
        break
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`
        break
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
        break
      case "copy":
        navigator.clipboard.writeText(window.location.href)
        alert("Link copied to clipboard!")
        break
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank", "width=600,height=400")
    }
  }

  // const handleSubmitComment = async (e: React.FormEvent) => {
  //   e.preventDefault()
  //   if (!newComment.trim()) return

  //   setIsSubmittingComment(true)

  //   // Simulate API call
  //   await new Promise((resolve) => setTimeout(resolve, 1000))

  //   const comment = {
  //     id: comments.length + 1,
  //     author: "You", // In a real app, this would come from user authentication
  //     authorImage: "/placeholder.svg?height=32&width=32&text=You",
  //     content: newComment,
  //     date: new Date().toLocaleDateString("en-US", {
  //       year: "numeric",
  //       month: "long",
  //       day: "numeric",
  //     }),
  //     likes: 0,
  //   }

  //   setComments((prev) => [...prev, comment])
  //   setNewComment("")
  //   setIsSubmittingComment(false)
  // }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link
              href="/blog"
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm sm:text-base font-bold">Back to Blog</span>
            </Link>

            {/* <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Blog</h1> */}

            <div className="flex items-center space-x-4">
              {/* <button
                onClick={handleLike}
                className={`flex items-center space-x-1 px-3 py-1 rounded-full transition-colors ${
                  isLiked ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Heart className={`h-4 w-4 ${isLiked ? "fill-current" : ""}`} />
                <span className="text-sm">{likesCount}</span>
              </button> */}

              {/* <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                <Share2 className="h-4 w-4 text-gray-600" />
              </button> */}
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Article Header */}
        <article className="max-w-4xl mx-auto">
          <header className="mb-8 sm:mb-12">
            <div className="flex items-center space-x-4 mb-4 sm:mb-6">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                {article.tag}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6 sm:mb-8">
              {article.title}
            </h1>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <Image
                  src={article ? article.imageUrl : "/placeholder.svg "}
                  alt={article ? article.author : ''}
                  width={40}
                  height={40}
                  className="rounded-full"
                />
                <div>
                  <p className="font-medium text-gray-900">{article.author}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <CalendarDays className="h-3 w-3" />
                      <span>{article.date}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Share Buttons */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 mr-2">Share:</span>
                <Link href ="" onClick={() => handleShare('twitter')} className="pointer"><FontAwesomeIcon icon={faXTwitter} className="fa-fw transition delay-50 duration-300 ease-in-out hover:-translate-y-1 hover:scale-150" size="sm" /></Link>
                <Link href ="" onClick={() => handleShare('facebook')} className="pointer"><FontAwesomeIcon icon={faFacebookF} className="fa-fw transition delay-50 duration-300 ease-in-out hover:-translate-y-1 hover:scale-150" size="sm" /></Link>
                <Link href ="" onClick={() => handleShare('linkedin')} className="pointer"><FontAwesomeIcon icon={faLinkedinIn} className="fa-fw transition delay-50 duration-300 ease-in-out hover:-translate-y-1 hover:scale-150" size="sm" /></Link>
                <Link href ="" onClick={() => handleShare('copy')} className="pointer"><FontAwesomeIcon icon={faLink} className="fa-fw transition delay-50 duration-300 ease-in-out hover:-translate-y-1 hover:scale-150" size="sm" /></Link>
              </div>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-8 sm:mb-12">
            <Image
              src={article.imageUrl || "/placeholder.svg"}
              alt={article.title}
              width={800}
              height={400}
              className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover rounded-xl shadow-lg"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose prose-lg max-w-none mb-12 sm:mb-16 text-justify blogContent"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Article Footer */}
          <footer className="border-t border-gray-100 pt-8 mb-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              {/* <div className="flex items-center space-x-6">
                <button
                  onClick={handleLike}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                    isLiked ? "bg-red-50 text-red-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <Heart className={`h-5 w-5 ${isLiked ? "fill-current" : ""}`} />
                  <span>{likesCount} likes</span>
                </button>

                <div className="flex items-center space-x-2 text-gray-500">
                  <MessageCircle className="h-5 w-5" />
                  <span>{comments.length} comments</span>
                </div>
              </div> */}

              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">Share this article:</span>
                <Link href ="" onClick={() => handleShare('twitter')} className="pointer"><FontAwesomeIcon icon={faXTwitter} className="fa-fw transition delay-50 duration-300 ease-in-out hover:-translate-y-1 hover:scale-150" size="sm" /></Link>
                <Link href ="" onClick={() => handleShare('facebook')} className="pointer"><FontAwesomeIcon icon={faFacebookF} className="fa-fw transition delay-50 duration-300 ease-in-out hover:-translate-y-1 hover:scale-150" size="sm" /></Link>
                <Link href ="" onClick={() => handleShare('linkedin')} className="pointer"><FontAwesomeIcon icon={faLinkedinIn} className="fa-fw transition delay-50 duration-300 ease-in-out hover:-translate-y-1 hover:scale-150" size="sm" /></Link>
                <Link href ="" onClick={() => handleShare('copy')} className="pointer"><FontAwesomeIcon icon={faLink} className="fa-fw transition delay-50 duration-300 ease-in-out hover:-translate-y-1 hover:scale-150" size="sm" /></Link>
              </div>
            </div>
          </footer>
        </article>

        {/* Comments Section */}
        {/* <section className="max-w-4xl mx-auto">
          <div className="border-t border-gray-100 pt-12"> */}
            {/* <h2 className="text-2xl font-bold text-gray-900 mb-8">Comments ({comments.length})</h2> */}

            {/* Comment Form */}
            {/* <form onSubmit={handleSubmitComment} className="mb-12">
              <div className="flex space-x-4">
                <Image
                  src="/placeholder.svg?height=40&width=40&text=You"
                  alt="Your avatar"
                  width={40}
                  height={40}
                  className="rounded-full flex-shrink-0"
                />
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Share your thoughts..."
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                  />
                  <div className="flex justify-end mt-3">
                    <button
                      type="submit"
                      disabled={!newComment.trim() || isSubmittingComment}
                      className="inline-flex items-center px-4 py-2 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmittingComment ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Posting...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Post Comment
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form> */}

            {/* Comments List */}
            {/* <div className="space-y-8">
              {comments.map((comment) => (
                <div key={comment.id} className="flex space-x-4">
                  <Image
                    src={comment.authorImage || "/placeholder.svg"}
                    alt={comment.author}
                    width={40}
                    height={40}
                    className="rounded-full flex-shrink-0"
                  />
                  <div className="flex-1">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{comment.author}</h4>
                        <span className="text-sm text-gray-500">{comment.date}</span>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{comment.content}</p>
                    </div>
                    <div className="flex items-center space-x-4 mt-2 ml-4">
                      <button className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                        <Heart className="h-3 w-3" />
                        <span>{comment.likes}</span>
                      </button>
                      <button className="text-sm text-gray-500 hover:text-gray-700 transition-colors">Reply</button>
                    </div>
                  </div>
                </div>
              ))}
            </div> */}
          {/* </div>
        </section> */}
      </main>
    </div>
  )
}
